import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  saveWazuhConnection,
  saveWazuhWebhook,
  testWazuhConnection,
  wazuhConnectionKey,
  wazuhConnectionQuery,
  type WazuhConnectionForm,
  type WazuhConnectionPage,
  type WazuhTestResult,
  type WazuhTestTarget,
  type WazuhWebhookAction,
} from '../../api/securiteeWazuhConnection'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings > Securitee > XDR > Wazuh connection: unde trimite ph4 evenimentele
 * EDR (syslog) si de unde citeste alertele (indexerul), pentru un grup SIEM.
 * O deseneaza `SiemGroupsSection`, pentru grupul deschis; serverul raspunde
 * 403 cui nu vede grupul.
 *
 * Parola nu vine niciodata de la server: campul e gol, iar `password_set`
 * spune doar ca exista una. Gol la salvare = ramane; "Remove password" o
 * sterge. Butoanele de test incearca ce e in formular, fara sa salveze.
 */

interface Draft {
  host: string
  port: string
  tls: boolean
  url: string
  user: string
  password: string
  clear: boolean
  insecure: boolean
  alert: string
}

interface FieldError {
  field: string
  message: string
}

type TestState = { busy: boolean; result: WazuhTestResult | null }

const IDLE: TestState = { busy: false, result: null }

/* Bifele nu stau in `.checkbox` de Bootstrap: foaia aplicatiei ii scoate marginea negativa si bifa cade peste text. */
const CHECK_LABEL = { display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 'normal', marginBottom: 0 } as const
const CHECK_INPUT = { position: 'static', margin: 0 } as const

function draftOf(page: WazuhConnectionPage): Draft {
  return {
    host: page.syslog.host,
    port: page.syslog.port === null ? '' : String(page.syslog.port),
    tls: page.syslog.tls,
    url: page.indexer.url,
    user: page.indexer.user,
    password: '',
    clear: false,
    insecure: page.indexer.insecure,
    alert: page.flow_alert?.minutes == null ? '' : String(page.flow_alert.minutes),
  }
}

/** Ce nu e numar la port pleaca asa cum e scris, ca serverul sa spuna ce e gresit. */
function bodyOf(draft: Draft): WazuhConnectionForm {
  const port = draft.port.trim()
  const alert = draft.alert.trim()
  return {
    syslog: { host: draft.host.trim(), port: port === '' ? null : /^\d+$/.test(port) ? Number(port) : port, tls: draft.tls },
    indexer: { url: draft.url.trim(), user: draft.user.trim(), password: draft.password, clear_password: draft.clear, insecure: draft.insecure },
    flow_alert_minutes: alert === '' ? null : /^\d+$/.test(alert) ? Number(alert) : alert,
  }
}

function fieldErrorOf(err: unknown): FieldError {
  const details = err instanceof ApiError ? err.details : undefined
  return {
    field: typeof details?.field === 'string' ? details.field : '',
    message: err instanceof Error ? err.message : 'Something went wrong.',
  }
}

/** Campurile cu eroarea lor; restul (`syslog`, `indexer`, `target`) merg in alerta de jos. */
const KNOWN_FIELDS = ['syslog.host', 'syslog.port', 'syslog.tls', 'indexer.url', 'indexer.user', 'indexer.password', 'indexer.insecure', 'flow_alert_minutes']

/**
 * Conexiunea unui grup SIEM. `heading` = titlul si explicatia (ecranul vechi);
 * in SIEM groups le da lista grupurilor.
 */
export function WazuhConnectionSection({ group = 1, heading = true }: { group?: number; heading?: boolean }) {
  const client = useQueryClient()
  const page = useQuery(wazuhConnectionQuery(group))
  // cheia formularului: se reface din raspuns dupa fiecare salvare reusita
  const [version, setVersion] = useState(0)
  const [saved, setSaved] = useState(false)

  if (page.error instanceof ApiError && page.error.status === 403) return null

  let body
  if (page.error) {
    body = <div className="alert alert-danger">Something went wrong: {(page.error as Error).message}</div>
  } else if (!page.data) {
    body = <Spinner />
  } else {
    body = (
      <WazuhForm
        key={version}
        group={group}
        page={page.data}
        saved={saved}
        onChange={() => setSaved(false)}
        onSaved={(next) => {
          client.setQueryData(wazuhConnectionKey(group), next)
          setSaved(true)
          setVersion((v) => v + 1)
        }}
      />
    )
  }
  const webhook = page.data?.webhook ? <WebhookPanel group={group} page={page.data} onSaved={(next) => client.setQueryData(wazuhConnectionKey(group), next)} /> : null

  if (!heading) {
    return (
      <section className="xdr-wazuh" aria-label="Wazuh connection">
        {body}
        {webhook}
      </section>
    )
  }

  return (
    <section className="xdr-wazuh" aria-labelledby="xdr-wazuh-title" style={{ marginTop: 30 }}>
      <h4 className="bordered" id="xdr-wazuh-title">
        Wazuh connection
      </h4>
      <p className="text-muted" style={{ fontSize: 12 }}>
        One connection for the whole platform, not only this company. Only platform administrators see this.
      </p>
      {body}
      {webhook}
    </section>
  )
}

function WazuhForm({
  group,
  page,
  saved,
  onChange,
  onSaved,
}: {
  group: number
  page: WazuhConnectionPage
  saved: boolean
  onChange: () => void
  onSaved: (next: WazuhConnectionPage) => void
}) {
  const [initial] = useState(() => draftOf(page))
  const [draft, setDraft] = useState(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<FieldError | null>(null)
  const [tests, setTests] = useState<Record<WazuhTestTarget, TestState>>({ syslog: IDLE, indexer: IDLE })
  const dirty = JSON.stringify(bodyOf(draft)) !== JSON.stringify(bodyOf(initial))

  const update = (patch: Partial<Draft>) => {
    setDraft({ ...draft, ...patch })
    setError(null)
    onChange()
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!dirty || busy) return
    setBusy(true)
    setError(null)
    try {
      onSaved(await saveWazuhConnection(bodyOf(draft), group))
    } catch (err) {
      setError(fieldErrorOf(err))
    } finally {
      setBusy(false)
    }
  }

  const runTest = async (target: WazuhTestTarget) => {
    setTests((t) => ({ ...t, [target]: { busy: true, result: null } }))
    setError(null)
    try {
      const result = await testWazuhConnection(target, bodyOf(draft), group)
      setTests((t) => ({ ...t, [target]: { busy: false, result } }))
    } catch (err) {
      const problem = fieldErrorOf(err)
      if (KNOWN_FIELDS.includes(problem.field)) {
        setError(problem)
        setTests((t) => ({ ...t, [target]: IDLE }))
      } else {
        setTests((t) => ({ ...t, [target]: { busy: false, result: { ok: false, message: problem.message, ms: 0 } } }))
      }
    }
  }

  /** Mesajul 422 sub campul lui, legat cu aria-describedby. */
  const fieldProps = (name: string) => {
    const bad = error?.field === name
    return { 'aria-invalid': bad ? ('true' as const) : undefined, 'aria-describedby': bad ? errorId(name) : undefined }
  }
  const fieldMessage = (name: string): ReactNode =>
    error?.field === name ? (
      <span className="help-block xdr-wazuh-field-error" id={errorId(name)}>
        {error.message}
      </span>
    ) : null
  const groupClass = (name: string, cols: string) => `${cols} form-group` + (error?.field === name ? ' has-error' : '')

  const passwordKept = page.indexer.password_set && !draft.clear
  const flow = page.flow
  const generalError = error !== null && !KNOWN_FIELDS.includes(error.field)

  return (
    <form className="xdr-wazuh-form" noValidate onSubmit={(e) => void submit(e)}>
      {!page.crypto_ready && (
        <div className="alert alert-warning xdr-wazuh-crypto">Passwords cannot be stored: encryption is not configured on the server.</div>
      )}

      <h5 className="xdr-wazuh-part" style={{ marginTop: 15 }}>
        <strong>Sending events (syslog)</strong>
      </h5>
      <LastUse last={flow?.last_forward_at ?? null} label="Last sent to Wazuh" />
      <div className="row">
        <div className={groupClass('syslog.host', 'col-sm-6')}>
          <label htmlFor="xdr-wazuh-host">Host</label>
          <input
            id="xdr-wazuh-host"
            type="text"
            className="form-control"
            autoComplete="off"
            spellCheck={false}
            placeholder="wazuh.example.net or 10.0.0.5"
            value={draft.host}
            disabled={busy}
            onChange={(e) => update({ host: e.target.value })}
            {...fieldProps('syslog.host')}
          />
          {fieldMessage('syslog.host')}
        </div>
        <div className={groupClass('syslog.port', 'col-sm-3')}>
          <label htmlFor="xdr-wazuh-port">Port</label>
          <input
            id="xdr-wazuh-port"
            type="text"
            inputMode="numeric"
            className="form-control"
            placeholder={draft.tls ? '6514' : '514'}
            value={draft.port}
            disabled={busy}
            onChange={(e) => update({ port: e.target.value })}
            {...fieldProps('syslog.port')}
          />
          {fieldMessage('syslog.port') ?? <span className="help-block">Empty = 514, or 6514 with TLS.</span>}
        </div>
        <div className={groupClass('syslog.tls', 'col-sm-3')}>
          <label style={{ ...CHECK_LABEL, marginTop: 30 }}>
            <input type="checkbox" style={CHECK_INPUT} checked={draft.tls} disabled={busy} onChange={(e) => update({ tls: e.target.checked })} {...fieldProps('syslog.tls')} />
            Use TLS
          </label>
          {fieldMessage('syslog.tls')}
        </div>
      </div>
      <TestRow target="syslog" label="sending events" state={tests.syslog} disabled={busy} onTest={(t) => void runTest(t)} />

      <h5 className="xdr-wazuh-part" style={{ marginTop: 20 }}>
        <strong>Reading alerts (indexer)</strong>
      </h5>
      <LastUse last={flow?.last_alert_read_at ?? null} label="Last alert read" />
      <div className="row">
        <div className={groupClass('indexer.url', 'col-sm-6')}>
          <label htmlFor="xdr-wazuh-url">Indexer URL</label>
          <input
            id="xdr-wazuh-url"
            type="url"
            className="form-control"
            autoComplete="off"
            spellCheck={false}
            placeholder="https://indexer:9200"
            value={draft.url}
            disabled={busy}
            onChange={(e) => update({ url: e.target.value })}
            {...fieldProps('indexer.url')}
          />
          {fieldMessage('indexer.url')}
        </div>
        <div className={groupClass('indexer.user', 'col-sm-3')}>
          <label htmlFor="xdr-wazuh-user">User</label>
          <input
            id="xdr-wazuh-user"
            type="text"
            className="form-control"
            autoComplete="off"
            spellCheck={false}
            value={draft.user}
            disabled={busy}
            onChange={(e) => update({ user: e.target.value })}
            {...fieldProps('indexer.user')}
          />
          {fieldMessage('indexer.user')}
        </div>
        <div className={groupClass('indexer.password', 'col-sm-3')}>
          <label htmlFor="xdr-wazuh-password">Password</label>
          <input
            id="xdr-wazuh-password"
            type="password"
            className="form-control"
            autoComplete="new-password"
            placeholder={passwordKept ? 'set - leave empty to keep' : ''}
            value={draft.password}
            disabled={busy}
            onChange={(e) => update({ password: e.target.value, clear: false })}
            {...fieldProps('indexer.password')}
          />
          {fieldMessage('indexer.password')}
          {passwordKept && (
            <button type="button" className="btn btn-link btn-xs xdr-wazuh-remove" style={{ paddingLeft: 0 }} disabled={busy} onClick={() => update({ clear: true, password: '' })}>
              Remove password
            </button>
          )}
          {draft.clear && (
            <span className="help-block xdr-wazuh-cleared">
              The password will be removed when you save.{' '}
              <button type="button" className="btn btn-link btn-xs" style={{ padding: 0, verticalAlign: 'baseline' }} onClick={() => update({ clear: false })}>
                Undo
              </button>
            </span>
          )}
        </div>
      </div>
      <div className={'form-group' + (error?.field === 'indexer.insecure' ? ' has-error' : '')}>
        <label style={CHECK_LABEL}>
          <input type="checkbox" style={CHECK_INPUT} checked={draft.insecure} disabled={busy} onChange={(e) => update({ insecure: e.target.checked })} {...fieldProps('indexer.insecure')} />
          Skip certificate check (insecure)
        </label>
        {fieldMessage('indexer.insecure')}
        {draft.insecure && (
          <span className="help-block text-danger xdr-wazuh-insecure">
            The certificate of the indexer will not be checked: anyone between the server and the indexer could read the password and the alerts. Use it only for a test setup.
          </span>
        )}
      </div>
      <TestRow target="indexer" label="reading alerts" state={tests.indexer} disabled={busy} onTest={(t) => void runTest(t)} />

      {page.flow_alert && (
        <div className={'form-group' + (error?.field === 'flow_alert_minutes' ? ' has-error' : '')} style={{ maxWidth: 320, marginTop: 15 }}>
          <label htmlFor="xdr-wazuh-flow-alert">Show "XDR flow stopped" after</label>
          <input
            id="xdr-wazuh-flow-alert"
            type="number"
            className="form-control"
            min={page.flow_alert.min}
            max={page.flow_alert.max}
            step={1}
            placeholder={String(page.flow_alert.default)}
            value={draft.alert}
            disabled={busy}
            onChange={(e) => update({ alert: e.target.value })}
            {...fieldProps('flow_alert_minutes')}
          />
          {fieldMessage('flow_alert_minutes')}
          <span className="help-block">
            Default {page.flow_alert.default} minutes. Between {page.flow_alert.min} and {page.flow_alert.max}. The red band in Incidents and Detections appears when events wait this long to be sent, or alerts are not read for this long.
          </span>
        </div>
      )}

      {generalError && <div className="alert alert-danger xdr-wazuh-error">{error.message}</div>}
      {saved && error === null && <div className="alert alert-success xdr-wazuh-saved">Wazuh connection saved. It applies from the next events sent and the next alerts read.</div>}

      <button type="submit" className="btn btn-primary" aria-label="Save Wazuh connection" disabled={!dirty || busy} style={{ marginTop: 10 }}>
        Save
      </button>
      {page.updated_at !== null && (
        <p className="text-muted xdr-wazuh-updated" style={{ fontSize: 12, marginTop: 10 }}>
          Last changed {page.updated_at}
          {page.updated_by_name !== '' ? ` by ${page.updated_by_name}` : ''}.
        </p>
      )}
    </form>
  )
}

function errorId(field: string): string {
  return 'xdr-wazuh-error-' + field.replace('.', '-')
}

/**
 * Ultima urma lasata de partea asta, cand serverul o da. De unde isi ia
 * platforma valorile (`source` din raspuns) NU se arata: e structura din
 * spate, nu ceva ce omul poate schimba de aici.
 */
function LastUse({ last, label }: { last: string | null; label: string }) {
  if (last === null) return null
  return (
    <p className="help-block xdr-wazuh-flow" style={{ fontSize: 12, marginTop: 0 }}>
      {label}: {last}
    </p>
  )
}

function TestRow({
  target,
  label,
  state,
  disabled,
  onTest,
}: {
  target: WazuhTestTarget
  label: string
  state: TestState
  disabled: boolean
  onTest: (target: WazuhTestTarget) => void
}) {
  return (
    <div className="xdr-wazuh-test" style={{ marginBottom: 10 }}>
      <button type="button" className="btn btn-default btn-sm" aria-label={`Test connection: ${label}`} disabled={disabled || state.busy} onClick={() => onTest(target)}>
        {state.busy ? 'Testing...' : 'Test connection'}
      </button>
      <div aria-live="polite">
        {state.result && (
          <div className={'alert ' + (state.result.ok ? 'alert-success' : 'alert-danger') + ' xdr-wazuh-result'} style={{ marginTop: 8, marginBottom: 0, padding: '6px 10px' }}>
            <i className={state.result.ok ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'} aria-hidden="true"></i> <span>{state.result.message}</span>
          </div>
        )}
      </div>
    </div>
  )
}

/** Fragmentul pentru ossec.conf (integrarea `shuffle`, vine cu Wazuh: fara script). */
function wazuhSnippet(url: string): string {
  return ['<integration>', '  <name>shuffle</name>', `  <hook_url>${url}</hook_url>`, '  <group>optimumdesk</group>', '  <alert_format>json</alert_format>', '</integration>'].join('\n')
}

/** Alertele din Wazuh prin webhook: URL-ul (aratat o data, la generare) si IP-urile permise. */
function WebhookPanel({ group, page, onSaved }: { group: number; page: WazuhConnectionPage; onSaved: (next: WazuhConnectionPage) => void }) {
  const hook = page.webhook!
  const [ips, setIps] = useState(hook.ips)
  const [fresh, setFresh] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<'replace' | 'remove' | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<FieldError | null>(null)

  const run = async (body: WazuhWebhookAction) => {
    setBusy(true)
    setError(null)
    try {
      const next = await saveWazuhWebhook(body, group)
      onSaved(next)
      if (body.action === 'generate') setFresh(next.webhook?.url ?? null)
      if (body.action === 'clear') setFresh(null)
      if (body.action === 'ips') setIps(next.webhook?.ips ?? '')
      setConfirm(null)
    } catch (err) {
      const details = err instanceof ApiError ? err.details : undefined
      setError({ field: typeof details?.field === 'string' ? details.field : '', message: err instanceof Error ? err.message : 'Something went wrong.' })
    } finally {
      setBusy(false)
    }
  }
  const copy = (text: string) => void navigator.clipboard?.writeText(text)

  return (
    <div className="xdr-wazuh-webhook" style={{ marginTop: 20, paddingTop: 12, borderTop: '1px solid #e3e8ee' }}>
      <h5 style={{ fontWeight: 600 }}>Alerts from Wazuh (webhook)</h5>
      <p className="text-muted" style={{ fontSize: 12 }}>
        Wazuh sends each OptimumDesk alert here the moment it fires, so incidents and tickets open within seconds. Every 5 minutes, alerts that did not arrive are read from the indexer.
      </p>
      <p className="xdr-wazuh-webhook-state">
        {hook.token_set ? (
          <span className="color-green">
            <i className="fa fa-check-circle" aria-hidden="true"></i> Set up. The URL was shown once, when it was generated.
          </span>
        ) : (
          <span className="font-grey-cascade">Not set up. Alerts are read from the indexer every few minutes.</span>
        )}
      </p>
      {hook.base_url === '' && <div className="alert alert-warning">The address of the OptimumDesk API is not set on the portal server, so the URL cannot be built.</div>}

      {fresh && (
        <div className="alert alert-warning xdr-wazuh-webhook-new">
          <strong>Copy it now: it is shown only this once.</strong> Give it to whoever configures Wazuh; put the block below on every Wazuh node that analyses events.
          <div className="input-group" style={{ marginTop: 8 }}>
            <input className="form-control input-sm" readOnly value={fresh} aria-label="Webhook URL" onFocus={(e) => e.target.select()} />
            <span className="input-group-btn">
              <button type="button" className="btn btn-default btn-sm" onClick={() => copy(fresh)}>
                Copy
              </button>
            </span>
          </div>
          <pre aria-label="Wazuh configuration" style={{ marginTop: 8, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {wazuhSnippet(fresh)}
          </pre>
          <button type="button" className="btn btn-default btn-xs" onClick={() => copy(wazuhSnippet(fresh))}>
            Copy configuration
          </button>
        </div>
      )}

      {error && error.field !== 'webhook.ips' && <div className="alert alert-danger">{error.message}</div>}

      {confirm ? (
        <div className="alert alert-danger xdr-wazuh-webhook-confirm" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <span>{confirm === 'remove' ? 'Wazuh stops sending alerts here; they are read from the indexer again.' : 'The current URL stops working right away; Wazuh must get the new one.'}</span>
          <button type="button" className="btn btn-danger btn-xs" disabled={busy} onClick={() => void run(confirm === 'remove' ? { action: 'clear' } : { action: 'generate' })}>
            {confirm === 'remove' ? 'Yes, remove it' : 'Yes, replace it'}
          </button>
          <button type="button" className="btn btn-default btn-xs" disabled={busy} onClick={() => setConfirm(null)}>
            Cancel
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {hook.token_set ? (
            <>
              <button type="button" className="btn btn-default btn-sm" disabled={busy || hook.base_url === ''} onClick={() => setConfirm('replace')} aria-label="Replace webhook URL">
                Replace URL
              </button>
              <button type="button" className="btn btn-default btn-sm" disabled={busy} onClick={() => setConfirm('remove')} aria-label="Remove webhook URL">
                Remove
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || hook.base_url === ''} onClick={() => void run({ action: 'generate' })} aria-label="Generate webhook URL">
              Generate URL
            </button>
          )}
        </div>
      )}

      <div className={'form-group' + (error?.field === 'webhook.ips' ? ' has-error' : '')} style={{ marginTop: 14, maxWidth: 520 }}>
        <label htmlFor="xdr-wazuh-webhook-ips">Allowed Wazuh IPs</label>
        <textarea id="xdr-wazuh-webhook-ips" className="form-control" rows={2} value={ips} disabled={busy} onChange={(e) => setIps(e.target.value)} />
        <span className="help-block">{error?.field === 'webhook.ips' ? error.message : 'IP addresses or networks, separated by commas. Empty = any address (the URL still has to match).'}</span>
        <button type="button" className="btn btn-default btn-sm" disabled={busy || ips === hook.ips} onClick={() => void run({ action: 'ips', ips })} aria-label="Save allowed IPs">
          Save IPs
        </button>
      </div>
    </div>
  )
}
